import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { createApiCall } from 'services';
import { Theme } from 'settings';

type UseGetCompanyStylesParams = {
  companyId: string | undefined;
};

type useGetCompanyStylesResponse = {
  // `Theme` carries `logo` as of VERA-304 — the API merges the brand's own mark
  // into this payload from `companies.logo`, so the loader and the theme arrive
  // in one round trip rather than waiting on the certificate.
  styles: Theme;
  /**
   * VRG-500 — the brand's display name (`companies.name`).
   *
   * Absent, never '' — the API normalises it, so a consumer's fallback is one
   * check. It is absent for the whole of an old cached response too, which is
   * why every caller has to word itself to work without a name rather than
   * treating the name as guaranteed once `companyId` is known.
   */
  name?: string;
  /**
   * VRG-309 — present only for an acquisition demo company. The API has always
   * returned it on this route (both public style endpoints share one use-case);
   * it simply had not been typed here.
   *
   * ⚠️ ENRICHMENT, NOT THE TRIGGER, exactly as on `useGetCompanyBySubdomain`.
   * Whether the demo disclosure renders is decided by the hostname and the
   * certificate's `example_kind`, never by this field — the lookup behind it
   * swallows its own failures, so keying the disclosure off it would fail OPEN.
   * `DemoBanner` reads it only to NAME the brand.
   */
  demo?: {
    brandName?: string;
  };
  /**
   * The brand's Settings → Features → "Certificate access" toggles
   * (`certificate_trigger_settings.collect_email` / `.collect_location`).
   *
   * ⚠️ ABSENT means UNRESOLVED and callers must fail CLOSED — ask for nothing.
   * It is absent only when the API's own lookup failed; a company that has never
   * configured the toggles gets `{collect_email: true, collect_location: true}`
   * explicitly, because that is the schema default and the platform's own
   * reading. Consume this through `useCertificateAccessSettings`, which encodes
   * that distinction, rather than off the raw payload.
   */
  access?: {
    collect_email: boolean;
    collect_location: boolean;
  };
};

const getCompanySettings = async (companyId: string | undefined): Promise<useGetCompanyStylesResponse | null> => {
  if (!companyId) return null;

  const response = await createApiCall({
    method: 'GET',
    url: `/companies/public/${companyId}/settings/style`,
  });

  return response.data as useGetCompanyStylesResponse;
};

export const useGetCompanyStyles = (
  params: UseGetCompanyStylesParams
): UseQueryResult<useGetCompanyStylesResponse | null, Error> => {
  const { companyId } = params;

  return useQuery({
    queryKey: ['companyStyles', companyId],
    queryFn: () => getCompanySettings(companyId),
  });
};
